import { BomReasoningRow } from '../../../types/bomVersions';

/**
 * Pure view helpers for the Reasoning sheet: who a row is credited to and its
 * change as field / before / after. Rows from versions recorded before actors
 * (or structured changes) existed still render — by `createdBy`, and by the
 * server's one-line `change`.
 */

export interface RowActor {
  kind: 'robbie' | 'buyer' | 'system' | 'unknown';
  /** The name shown: "Robbie", the buyer's name, "Buyer", "System", "—". */
  label: string;
  /** robbie only: who approved it. */
  approvedBy?: string;
}

export const rowActor = (row: Pick<BomReasoningRow, 'actor' | 'actorName' | 'createdBy'>): RowActor => {
  switch (row.actor) {
    case 'robbie':
      return { kind: 'robbie', label: 'Robbie', approvedBy: row.actorName || 'the buyer' };
    case 'buyer':
      return { kind: 'buyer', label: row.actorName || 'Buyer' };
    case 'system':
      return { kind: 'system', label: 'System' };
    default:
      return row.createdBy ? { kind: 'buyer', label: 'Buyer' } : { kind: 'unknown', label: '—' };
  }
};

const FIELD_LABELS: Record<string, string> = {
  component: 'Component',
  shortlistedSuppliers: 'Shortlist',
  name: 'Name',
  partNumber: 'Part number',
  mpn: 'MPN',
  manufacturer: 'Manufacturer',
  description: 'Description',
  specifications: 'Specifications',
  quantity: 'Qty',
  targetPrice: 'Target price',
};

export const fieldLabel = (field: string): string => FIELD_LABELS[field] || field;

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export interface ChangeParts {
  field: string | null;
  before: string;
  after: string;
}

/** The change split into parts, or null when only the one-line form exists. */
export const changeParts = (row: BomReasoningRow): ChangeParts | null => {
  if (row.field === undefined && row.before === undefined && row.after === undefined) return null;
  if (!row.field && (row.after === null || row.after === undefined) && (row.before === null || row.before === undefined)) {
    return null;
  }
  return { field: row.field ? fieldLabel(row.field) : null, before: valueText(row.before), after: valueText(row.after) };
};
